// Curated catalogue of pre-built sounds for the Sound Vault panel.
//
// Three entry types:
//   - 'sample' — single drum/sample voice   → const kick = s("bd*4")
//   - 'synth'  — single pitched synth voice → const lead = note("c5").s("sawtooth")
//   - 'section' — multi-voice song-structure template that mints
//                 a stack(...) const (intro / buildup / drop / …)
//
// Each entry knows how to (a) render itself on a card and (b) mint
// a ready-to-insert top-level statement. Section templates are
// grounded in EDM convention (four-on-floor intro, accelerating
// snare-roll buildup, drums-dropout breakdown, etc.) so users can
// scaffold a working arrangement quickly.

import { patternFromString } from '../mini';
import {
  newBlockId,
  type Effect,
  type Expression,
  type StackBlock,
  type Statement,
  type VoiceBlock,
} from '../types';

export type SoundCategory =
  | 'structure'
  | 'drums'
  | 'perc'
  | 'bass'
  | 'lead'
  | 'keys'
  | 'pad'
  | 'fx';

export type GlyphKind =
  // single-sound glyphs
  | 'kick'
  | 'snare'
  | 'hat'
  | 'clap'
  | 'tom'
  | 'cymbal'
  | 'shaker'
  | 'tabla'
  | 'saw'
  | 'square'
  | 'sine'
  | 'triangle'
  | 'supersaw'
  | 'pulse'
  | 'noise'
  | 'bell'
  | 'piano'
  // section glyphs
  | 'sectionSparse'
  | 'sectionGroove'
  | 'sectionBuild'
  | 'sectionDrop'
  | 'sectionBreak'
  | 'sectionBridge';

// Per-voice config inside a section template. Kept intentionally
// narrow: pattern + gain + an optional effect or two. Anything
// fancier belongs in the block editor after the user inserts it.
export type VoiceSpec =
  | {
      kind: 'sample';
      pattern: string;
      gain: number;
      lpf?: number;
      hpf?: number;
      room?: number;
    }
  | {
      kind: 'synth';
      pattern: string;
      wave: string;
      gain: number;
      lpf?: number;
      hpf?: number;
      room?: number;
    };

type BaseFields = {
  id: string;
  label: string;
  varName: string;
  blurb: string;
  category: SoundCategory;
  tags: string[];
  glyph: GlyphKind;
};

export type SoundEntry = BaseFields &
  (
    | {
        type: 'sample';
        sample: string;
        pattern: string;
        gain: number;
      }
    | {
        type: 'synth';
        sample: string;
        pattern: string;
        gain: number;
      }
    | {
        type: 'section';
        voices: VoiceSpec[];
        /** Human-readable duration hint shown on the card (e.g. "16 bars"). */
        duration: string;
      }
  );

export const CATEGORY_ORDER: SoundCategory[] = [
  'structure',
  'drums',
  'perc',
  'bass',
  'lead',
  'keys',
  'pad',
  'fx',
];

export const CATEGORY_META: Record<
  SoundCategory,
  { label: string; accent: string; dot: string; glow: string }
> = {
  structure: {
    label: 'Parts',
    accent: 'bg-yellow-300',
    dot: 'text-yellow-300',
    glow: 'hover:shadow-[0_0_0_1px_rgba(253,224,71,0.45),0_8px_24px_-6px_rgba(253,224,71,0.25)]',
  },
  drums: {
    label: 'Drums',
    accent: 'bg-amber-400',
    dot: 'text-amber-400',
    glow: 'hover:shadow-[0_0_0_1px_rgba(251,191,36,0.35),0_8px_24px_-6px_rgba(251,191,36,0.18)]',
  },
  perc: {
    label: 'Perc',
    accent: 'bg-orange-400',
    dot: 'text-orange-400',
    glow: 'hover:shadow-[0_0_0_1px_rgba(251,146,60,0.35),0_8px_24px_-6px_rgba(251,146,60,0.18)]',
  },
  bass: {
    label: 'Bass',
    accent: 'bg-rose-400',
    dot: 'text-rose-400',
    glow: 'hover:shadow-[0_0_0_1px_rgba(251,113,133,0.35),0_8px_24px_-6px_rgba(251,113,133,0.18)]',
  },
  lead: {
    label: 'Lead',
    accent: 'bg-cyan-300',
    dot: 'text-cyan-300',
    glow: 'hover:shadow-[0_0_0_1px_rgba(103,232,249,0.35),0_8px_24px_-6px_rgba(103,232,249,0.18)]',
  },
  keys: {
    label: 'Keys',
    accent: 'bg-violet-300',
    dot: 'text-violet-300',
    glow: 'hover:shadow-[0_0_0_1px_rgba(196,181,253,0.35),0_8px_24px_-6px_rgba(196,181,253,0.18)]',
  },
  pad: {
    label: 'Pad',
    accent: 'bg-emerald-300',
    dot: 'text-emerald-300',
    glow: 'hover:shadow-[0_0_0_1px_rgba(110,231,183,0.35),0_8px_24px_-6px_rgba(110,231,183,0.18)]',
  },
  fx: {
    label: 'FX',
    accent: 'bg-fuchsia-300',
    dot: 'text-fuchsia-300',
    glow: 'hover:shadow-[0_0_0_1px_rgba(240,171,252,0.35),0_8px_24px_-6px_rgba(240,171,252,0.18)]',
  },
};

// ─── Catalog ────────────────────────────────────────────────────────

export const SOUND_CATALOG: SoundEntry[] = [
  // ── Song parts ───────────────────────────────────────────────────
  // Instrumentation choices mirror EDM/house conventions at ~120-128
  // BPM: intro is DJ-mixable (kick + offbeat hats, no lead); buildup
  // substitutes snare-rolls + noise riser for kick; breakdown pulls
  // drums entirely; drop is the full-energy payload with open-hats +
  // crash + lead. Each template ends up as `const <name> = stack(…)`.
  section(
    'section-intro',
    'Intro',
    'intro',
    'DJ-friendly opener: 4-on-floor kick, offbeat hats, filtered pad',
    ['intro', 'opener', 'dj', 'sparse'],
    'sectionSparse',
    '16 bars',
    [
      drum('bd*4', 0.85),
      drum('~ hh ~ hh', 0.42),
      synthVoice('<c3,eb3,g3>', 'sawtooth', 0.4, { lpf: 600 }),
    ],
  ),
  section(
    'section-buildup',
    'Buildup',
    'buildup',
    'Tension: accelerating snare roll + white-noise riser',
    ['buildup', 'riser', 'build', 'tension'],
    'sectionBuild',
    '8 bars',
    [
      // Cycles through densities each bar → the "accelerating
      // snare roll" feel using pure mini-notation, so the
      // structured block insertion matches the preview exactly.
      drum('<sd*2 sd*4 sd*8 sd*16>', 0.7),
      drum('white*4', 0.3),
      drum('bd*4', 0.85),
      drum('cr ~ ~ ~', 0.5),
    ],
  ),
  section(
    'section-groove',
    'Groove',
    'groove',
    'Main pocket: kick + hats + clap on 2 & 4 + driving bass',
    ['groove', 'main', 'verse', 'body'],
    'sectionGroove',
    '16 bars',
    [
      drum('bd*4', 0.9),
      drum('hh*8', 0.5),
      drum('~ cp ~ cp', 0.65),
      synthVoice('c2 c2 eb2 g2', 'sawtooth', 0.6),
    ],
  ),
  section(
    'section-drop',
    'Drop',
    'drop',
    'Peak energy: everything on — open hats, crash, lead hook',
    ['drop', 'climax', 'hook', 'full'],
    'sectionDrop',
    '16 bars',
    [
      drum('bd*4', 0.95),
      drum('hh*8', 0.45),
      drum('~ oh ~ oh', 0.5),
      drum('~ cp ~ cp', 0.75),
      drum('cr ~ ~ ~', 0.55),
      synthVoice('c2 c2 eb2 g2', 'sawtooth', 0.7),
      synthVoice('<c5 eb5 g5 bb5>', 'supersaw', 0.55),
    ],
  ),
  section(
    'section-breakdown',
    'Breakdown',
    'breakdown',
    'Drums out: sine pad swell + square lead motif',
    ['breakdown', 'strip', 'quiet', 'pad'],
    'sectionBreak',
    '16 bars',
    [
      synthVoice('<c3,eb3,g3> <ab2,c3,eb3>', 'sine', 0.5),
      synthVoice('<c5 eb5 g5 c6>', 'square', 0.4, { lpf: 2400 }),
    ],
  ),
  section(
    'section-bridge',
    'Bridge',
    'bridge',
    'Contrasting variation: new chord root, thinner drums',
    ['bridge', 'contrast', 'variation'],
    'sectionBridge',
    '8 bars',
    [
      drum('bd ~ ~ bd', 0.85),
      drum('hh*4', 0.4),
      synthVoice('f2 f2 ab2 c3', 'sawtooth', 0.6),
      synthVoice('<f4 ab4 c5 f5>', 'square', 0.45),
    ],
  ),
  section(
    'section-outro',
    'Outro',
    'outro',
    'Mirror of the intro: thinning kick + fading pad for DJ mix-out',
    ['outro', 'ending', 'fade'],
    'sectionSparse',
    '16 bars',
    [
      drum('bd*4', 0.7),
      drum('~ hh ~ hh', 0.35),
      synthVoice('<c3,g3>', 'sine', 0.3),
    ],
  ),

  // ── Drums ────────────────────────────────────────────────────────
  sample('kick-909', 'Kick', 'kick', 'bd', '909-style four-on-the-floor', 'drums', ['kick', 'bd', 'four'], 'kick', 'bd*4', 0.95),
  sample('kick-half', 'Half-Time Kick', 'kick', 'bd', 'Lazy, weighty pulse', 'drums', ['kick', 'bd'], 'kick', 'bd ~ ~ ~', 0.95),
  sample('snare', 'Snare', 'snare', 'sd', 'Backbeat snare on 2 & 4', 'drums', ['snare', 'sd', 'backbeat'], 'snare', '~ sd ~ sd', 0.85),
  sample('rim', 'Rim', 'rim', 'rs', 'Dry rimshot accent', 'drums', ['rim', 'rs', 'click'], 'clap', '~ rs ~ rs', 0.8),
  sample('clap', 'Clap', 'clap', 'cp', 'Layered handclap on the 3', 'drums', ['clap', 'cp'], 'clap', '~ ~ cp ~', 0.75),
  sample('hats-closed', 'Closed Hat', 'hat', 'hh', 'Tight eighth-note hats', 'drums', ['hh', 'hat', 'hats'], 'hat', 'hh*8', 0.55),
  sample('hats-open', 'Open Hat', 'ohat', 'ho', 'Sustained open hihat', 'drums', ['ho', 'open', 'hat'], 'hat', '~ ho ~ ho', 0.6),
  sample('tom', 'Toms', 'tom', 'lt mt ht', 'Low → mid → high tom fill', 'drums', ['tom', 'fill'], 'tom', '~ ~ <lt mt ht> ~', 0.8),
  sample('cymbal', 'Crash', 'crash', 'cr', 'Crash cymbal accent', 'drums', ['cymbal', 'crash'], 'cymbal', 'cr ~ ~ ~', 0.6),

  // ── Percussion ──────────────────────────────────────────────────
  sample('tabla', 'Tabla', 'tabla', 'tabla', 'Indian hand-drum pattern', 'perc', ['tabla', 'world'], 'tabla', 'tabla(3,8)', 0.8),
  sample('conga', 'Conga', 'conga', 'east', 'Off-kilter conga groove', 'perc', ['conga', 'east'], 'tabla', '<east east:2 east:4>', 0.8),
  sample('shaker', 'Shaker', 'shaker', 'hc', 'Ghost-ride shuffle', 'perc', ['shaker', 'hc'], 'shaker', 'hc*16', 0.4),
  sample('cowbell', 'Cowbell', 'cb', 'cb', 'Dry cowbell tick', 'perc', ['cb', 'cowbell'], 'shaker', 'cb*4', 0.65),
  sample('metal', 'Metal', 'metal', 'metal', 'Industrial metallic ping', 'perc', ['metal', 'industrial'], 'cymbal', '~ metal ~ metal', 0.55),
  sample('tabla-lo', 'Tabla Low', 'tablalo', 'tabla:2', 'Deep tabla hit', 'perc', ['tabla', 'hand'], 'tabla', 'tabla:2(5,8)', 0.7),

  // ── Bass ────────────────────────────────────────────────────────
  synth('bass-saw', 'Saw Bass', 'bass', 'sawtooth', 'Gritty sub-saw riff', 'bass', ['saw', 'sub'], 'saw', 'c2 c2 eb2 g2', 0.65),
  synth('bass-square', 'Square Bass', 'bass', 'square', 'Chip-tune square bass', 'bass', ['square', 'chip'], 'square', '<c2 c2 eb2 g2>', 0.55),
  synth('bass-sub', 'Sub Sine', 'sub', 'sine', 'Deep sine sub-bass', 'bass', ['sine', 'sub'], 'sine', 'c2 ~ eb2 ~', 0.8),
  synth('bass-triangle', 'Tri Bass', 'tri', 'triangle', 'Soft triangle bass', 'bass', ['triangle'], 'triangle', 'c2 eb2 g2 bb2', 0.7),

  // ── Lead ────────────────────────────────────────────────────────
  synth('lead-saw', 'Saw Lead', 'lead', 'sawtooth', 'Bright sawtooth melody', 'lead', ['saw', 'lead'], 'saw', 'c5 e5 g5 b5', 0.55),
  synth('lead-square', 'Square Lead', 'lead', 'square', 'Classic chip lead', 'lead', ['square', 'chip'], 'square', '<c5 e5 g5 f5>', 0.5),
  synth('lead-supersaw', 'Supersaw', 'lead', 'supersaw', 'Detuned supersaw stack', 'lead', ['supersaw', 'trance'], 'supersaw', 'c5 g5 e5 g5', 0.5),
  synth('lead-pulse', 'Pulse', 'lead', 'pulse', 'Pulsed sharp lead', 'lead', ['pulse'], 'pulse', 'c5 eb5 g5 eb5', 0.5),

  // ── Keys ────────────────────────────────────────────────────────
  synth('keys-piano', 'Piano', 'piano', 'gm_acoustic_grand_piano', 'Grand-piano voicings', 'keys', ['piano', 'keys'], 'piano', '<c4,e4,g4> <f4,a4,c5>', 0.7),
  synth('keys-epiano', 'Electric Piano', 'epiano', 'gm_epiano1', 'Warm Rhodes-style keys', 'keys', ['rhodes', 'epiano'], 'piano', '<c4 eb4 g4> <bb3 eb4 g4>', 0.7),
  synth('keys-bell', 'Bell', 'bell', 'gm_celesta', 'Twinkling celeste bells', 'keys', ['bell', 'celesta'], 'bell', 'c5 e5 g5 c6', 0.65),

  // ── Pad ─────────────────────────────────────────────────────────
  synth('pad-sine', 'Sine Pad', 'pad', 'sine', 'Smooth sine-wave pad', 'pad', ['pad', 'sine', 'ambient'], 'sine', '<c3,e3,g3> <a2,c3,e3>', 0.55),
  synth('pad-tri', 'Triangle Pad', 'pad', 'triangle', 'Soft triangle pad', 'pad', ['pad', 'triangle'], 'triangle', '<c3,e3,g3,b3>', 0.55),
  synth('pad-saw', 'String Pad', 'strings', 'gm_string_ensemble_1', 'Lush string ensemble', 'pad', ['strings', 'pad'], 'piano', '<c3,e3,g3> <f3,a3,c4>', 0.6),

  // ── FX ──────────────────────────────────────────────────────────
  synth('fx-noise', 'White Noise', 'noise', 'white', 'White-noise riser / texture', 'fx', ['noise', 'white'], 'noise', 'c3*8', 0.4),
  synth('fx-pink', 'Pink Noise', 'pink', 'pink', 'Pink noise texture', 'fx', ['noise', 'pink'], 'noise', 'c3*8', 0.4),
  sample('fx-bird', 'Crow', 'crow', 'crow', 'Sampled crow caw', 'fx', ['crow', 'bird', 'sample'], 'piano', 'crow ~ ~ ~', 0.7),
  sample('fx-bass', 'Bassdrum FX', 'boom', 'bd:5', 'Alt kick for FX hits', 'fx', ['bd', 'fx'], 'kick', 'bd:5 ~ ~ ~', 0.9),
];

// ─── Entry factories ────────────────────────────────────────────────

function sample(
  id: string,
  label: string,
  varName: string,
  sampleName: string,
  blurb: string,
  category: SoundCategory,
  tags: string[],
  glyph: GlyphKind,
  pattern: string,
  gain: number,
): SoundEntry {
  return {
    id,
    label,
    varName,
    sample: sampleName,
    blurb,
    category,
    tags,
    glyph,
    type: 'sample',
    pattern,
    gain,
  };
}

function synth(
  id: string,
  label: string,
  varName: string,
  waveName: string,
  blurb: string,
  category: SoundCategory,
  tags: string[],
  glyph: GlyphKind,
  pattern: string,
  gain: number,
): SoundEntry {
  return {
    id,
    label,
    varName,
    sample: waveName,
    blurb,
    category,
    tags,
    glyph,
    type: 'synth',
    pattern,
    gain,
  };
}

function section(
  id: string,
  label: string,
  varName: string,
  blurb: string,
  tags: string[],
  glyph: GlyphKind,
  duration: string,
  voices: VoiceSpec[],
): SoundEntry {
  return {
    id,
    label,
    varName,
    blurb,
    category: 'structure',
    tags,
    glyph,
    type: 'section',
    voices,
    duration,
  };
}

// ─── VoiceSpec helpers (used only in section definitions above) ─────

function drum(
  pattern: string,
  gain: number,
  opts: { lpf?: number; hpf?: number; room?: number } = {},
): VoiceSpec {
  return {
    kind: 'sample',
    pattern,
    gain,
    ...(opts.lpf !== undefined && { lpf: opts.lpf }),
    ...(opts.hpf !== undefined && { hpf: opts.hpf }),
    ...(opts.room !== undefined && { room: opts.room }),
  };
}

function synthVoice(
  pattern: string,
  wave: string,
  gain: number,
  opts: { lpf?: number; hpf?: number; room?: number } = {},
): VoiceSpec {
  return {
    kind: 'synth',
    pattern,
    wave,
    gain,
    ...(opts.lpf !== undefined && { lpf: opts.lpf }),
    ...(opts.hpf !== undefined && { hpf: opts.hpf }),
    ...(opts.room !== undefined && { room: opts.room }),
  };
}

// ─── Statement minting ──────────────────────────────────────────────

export function makeSoundStatement(
  entry: SoundEntry,
  takenNames: Set<string>,
): Statement {
  const name = uniqueName(entry.varName, takenNames);
  if (entry.type === 'section') {
    return {
      id: newBlockId(),
      kind: 'const',
      name,
      expr: buildStack(entry.voices),
    };
  }
  return {
    id: newBlockId(),
    kind: 'const',
    name,
    expr: buildVoice(entry),
  };
}

function buildVoice(entry: SoundEntry): VoiceBlock {
  const voice = voiceFromEntry(entry);
  if (!voice) throw new Error('buildVoice called with section entry');
  return voice;
}

// Exported so the library's "insert into existing stack" path can
// build a bare VoiceBlock (no const wrapping) suitable for pushing
// into a StackBlock's children. Returns null for section entries —
// sections are structural containers and can't themselves be voices.
export function voiceFromEntry(entry: SoundEntry): VoiceBlock | null {
  if (entry.type === 'section') return null;
  const gainEffect: Effect = {
    id: newBlockId(),
    kind: 'gain',
    args: [{ kind: 'numeric', value: { kind: 'num', value: entry.gain } }],
  };
  if (entry.type === 'sample') {
    return {
      id: newBlockId(),
      kind: 'voice.s',
      pattern: patternFromString(entry.pattern),
      effects: [gainEffect],
    };
  }
  const sEffect: Effect = {
    id: newBlockId(),
    kind: 's',
    args: [{ kind: 'string', value: entry.sample }],
  };
  return {
    id: newBlockId(),
    kind: 'voice.note',
    pattern: patternFromString(entry.pattern),
    effects: [sEffect, gainEffect],
  };
}

function buildStack(voices: VoiceSpec[]): StackBlock {
  const children: Expression[] = voices.map(buildVoiceFromSpec);
  return {
    id: newBlockId(),
    kind: 'stack',
    children,
  };
}

function buildVoiceFromSpec(spec: VoiceSpec): VoiceBlock {
  const effects: Effect[] = [];

  if (spec.kind === 'synth') {
    effects.push({
      id: newBlockId(),
      kind: 's',
      args: [{ kind: 'string', value: spec.wave }],
    });
  }

  effects.push({
    id: newBlockId(),
    kind: 'gain',
    args: [{ kind: 'numeric', value: { kind: 'num', value: spec.gain } }],
  });

  if (spec.lpf !== undefined) {
    effects.push({
      id: newBlockId(),
      kind: 'lpf',
      args: [{ kind: 'numeric', value: { kind: 'num', value: spec.lpf } }],
    });
  }
  if (spec.hpf !== undefined) {
    effects.push({
      id: newBlockId(),
      kind: 'hpf',
      args: [{ kind: 'numeric', value: { kind: 'num', value: spec.hpf } }],
    });
  }
  if (spec.room !== undefined) {
    effects.push({
      id: newBlockId(),
      kind: 'room',
      args: [{ kind: 'numeric', value: { kind: 'num', value: spec.room } }],
    });
  }

  return {
    id: newBlockId(),
    kind: spec.kind === 'sample' ? 'voice.s' : 'voice.note',
    pattern: patternFromString(spec.pattern),
    effects,
  };
}

function uniqueName(base: string, taken: Set<string>): string {
  if (!taken.has(base)) return base;
  let i = 2;
  while (taken.has(`${base}${i}`)) i++;
  return `${base}${i}`;
}

// ─── Preview-code building ──────────────────────────────────────────
// A minimal Strudel snippet for quick auditions. Skips parse/codegen
// (library is a closed vocabulary we control, so no need to roundtrip).

const PREVIEW_CPM = 110;

export function previewCodeFor(entry: SoundEntry): string {
  if (entry.type === 'section') {
    const inner = entry.voices.map(voiceSpecToCode).join(',\n  ');
    return `setcpm(${PREVIEW_CPM});\nstack(\n  ${inner}\n)`;
  }
  const pattern = escapeQuotes(entry.pattern);
  if (entry.type === 'sample') {
    return `setcpm(${PREVIEW_CPM});\ns("${pattern}").gain(${entry.gain})`;
  }
  return `setcpm(${PREVIEW_CPM});\nnote("${pattern}").s("${entry.sample}").gain(${entry.gain})`;
}

function voiceSpecToCode(spec: VoiceSpec): string {
  const pattern = escapeQuotes(spec.pattern);
  let base =
    spec.kind === 'sample'
      ? `s("${pattern}")`
      : `note("${pattern}").s("${spec.wave}")`;
  base += `.gain(${spec.gain})`;
  if (spec.lpf !== undefined) base += `.lpf(${spec.lpf})`;
  if (spec.hpf !== undefined) base += `.hpf(${spec.hpf})`;
  if (spec.room !== undefined) base += `.room(${spec.room})`;
  return base;
}

function escapeQuotes(s: string): string {
  return s.replace(/"/g, '\\"');
}

// ─── Card-level display helper ──────────────────────────────────────
// Short one-line summary shown in the code-preview strip on each
// card. For sections, returns `stack(voiceA, voiceB, …)` truncated
// sensibly; for single voices, matches the actual generated form.

export function codeStripFor(entry: SoundEntry): string {
  if (entry.type === 'section') {
    const parts = entry.voices.map((v) =>
      v.kind === 'sample' ? `s("${v.pattern}")` : `note("${v.pattern}")`,
    );
    return `stack(${parts.join(', ')})`;
  }
  if (entry.type === 'sample') return `s("${entry.pattern}")`;
  return `note("${entry.pattern}").s("${entry.sample}")`;
}
